import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useCountryDetailByIdQuery, useCountryManagementUpdateMutation } from 'api';
import { Form, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { IManagementEditModal } from './index';

export const useManagementEditModal = (props: IManagementEditModal) => {
  const { onModalStateChange, modalState } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { data: countryDetails = {} } = useCountryDetailByIdQuery({ id });
  const [updateCountryManagement, { isLoading }] = useCountryManagementUpdateMutation();
  const [notificationApi, contextHolder] = notification.useNotification();

  const initialManagement: Record<string, string> = useMemo(
    () => ({
      salesManagerId: countryDetails.salesManagerId,
      salesManagerAssistantId: countryDetails.salesManagerAssistantId,
      purchaseManagerId: countryDetails.purchaseManagerId,
      purchaseManagerAssistantId: countryDetails.purchaseManagerAssistantId,
      postSalesManagerId: countryDetails.postSalesManagerId,
      postSalesManagerAssistantId: countryDetails.postSalesManagerAssistantId,
    }),
    [countryDetails]
  );

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleManagerChange = useCallback(
    (value: string, key: string) => {
      if (value === form.getFieldValue([key])) {
        form.setFieldsValue({ [key]: '' });
      }
    },
    [form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish = useCallback(() => {
    const payload = form.getFieldsValue() || {};
    const isFormChanged = Object.keys(payload).some((key) => initialManagement[key] !== payload[key]);

    if (!isFormChanged) {
      onCancel();
      return;
    }
    payload.countryId = id as string;
    updateCountryManagement(payload)
      .then(({ data, error }: any) => {
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: 'Country management was successfully updated.',
          });
          onCancel();
        } else if (!error.data.success) {
          const { data = {} } = error.data;
          Object.keys(data).forEach((_key: string) => {
            openNotificationWithIcon({
              type: 'error',
              message: data[_key],
            });
          });
        } else if (error.data.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      })
      .catch((err) => {
        console.log(err, 'error');
      });
  }, [form, updateCountryManagement, openNotificationWithIcon, id, onCancel, initialManagement]);

  useEffect(() => {
    if (initialManagement && modalState) {
      form.setFieldsValue(initialManagement);
    }
  }, [form, modalState, initialManagement]);

  return {
    onFinish,
    onCancel,
    handleManagerChange,
    contextHolder,
    form,
    isLoading,
  };
};
